import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Track,
  RemoteTrackPublication,
  RemoteParticipant,
  LocalTrackPublication,
  createLocalAudioTrack,
} from "livekit-client";
import {
  useTracks,
  useRoomContext,
  useLocalParticipant,
} from "@livekit/components-react";
import type { TrackReference } from "@livekit/components-core";
import { useLibrary } from "../../contexts/LibraryContext";
import { TextToSpeechService } from "../../lib/textToSpeech";
import type { Book as BookType } from "../../types/book";

// Extend the BookType to include content property
type BookWithContent = BookType & {
  content?: string[];
};

interface XAIVoice {
  id: string;
  voiceURI: string;
  name: string;
  lang: string;
  localService?: boolean;
  default?: boolean;
  gender?: string;
}

// Using TrackReference from @livekit/components-react

const HeaderIconsWithDropdowns = () => (
  <div className="header-icons">
    {/* Add your header icons and dropdowns here */}
  </div>
);

const Reading: React.FC = () => {
  // Router and context
  const { bookId: id } = useParams<{ bookId: string }>();
  const { books } = useLibrary();
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();

  // Refs
  const readingAreaRef = useRef<HTMLDivElement>(null);
  const ttsServiceRef = useRef<TextToSpeechService | null>(null);

  const [book, setBook] = useState<BookWithContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setConnecting] = useState(false);
  const [isConnected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackVersion, setTrackVersion] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalSections, setTotalSections] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState<XAIVoice | null>(null);
  const [availableVoices, setAvailableVoices] = useState<XAIVoice[]>([]);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const [microphoneTrack, setMicrophoneTrack] =
    useState<LocalTrackPublication | null>(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [continuousProgress, setContinuousProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Get audio tracks from LiveKit room with proper type safety
  const trackReferences = useTracks([
    { source: Track.Source.Microphone, withPlaceholder: false },
    { source: Track.Source.Camera, withPlaceholder: false },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
  ]).filter(
    (
      ref
    ): ref is TrackReference & {
      publication: RemoteTrackPublication;
    } => {
      return Boolean(ref.publication?.track);
    }
  );

  // Initialize TTS service when room is connected
  useEffect(() => {
    if (!room || room.state !== "connected") return;

    const service = new TextToSpeechService(room);
    ttsServiceRef.current = service;

    const handleProgress = (progress: number) => {
      setProgress(progress);
      setContinuousProgress(progress);
      // Update current section based on progress
      const sectionIndex = Math.floor(progress * service.getContentQueue().length);
      if (sectionIndex !== currentIndex && service.getContentQueue()[sectionIndex]) {
        setCurrentIndex(sectionIndex);
        setCurrentSection(service.getContentQueue()[sectionIndex]);
      }
    };
    
    // Set up word boundary callback for highlighting
    service.setWordBoundaryCallback((charIndex: number, word: string) => {
      console.log('[Reading] Word boundary:', { charIndex, word });
      setCurrentWordIndex(charIndex);
    });

    // Set up progress handler
    service.onProgress(handleProgress);
    service.setProgressCallback(handleProgress);
    
    // Start continuous progress updates when playing
    const startProgressUpdates = () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = setInterval(() => {
        if (isPlaying) {
          setContinuousProgress(prev => {
            const increment = 1 / (service.getContentQueue().length * 30); // Smooth increment
            return Math.min(prev + increment, 1);
          });
        }
      }, 100);
    };
    
    const stopProgressUpdates = () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
    
    // Expose progress control methods
    (service as any).startProgressUpdates = startProgressUpdates;
    (service as any).stopProgressUpdates = stopProgressUpdates;

    return () => {
      try {
        service.offProgress();
        service.cleanup();
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      } catch (error) {
        console.error("Error cleaning up TTS service:", error);
      } finally {
        ttsServiceRef.current = null;
      }
    };
  }, [room, isConnected]);

  useEffect(() => {
    const service = ttsServiceRef.current;
    if (!service) return;

    return () => {
      try {
        service.offProgress();
      } catch (error) {
        console.error("Error cleaning up TTS progress handlers:", error);
      }
    };
  }, []);

  const loadVoices = useCallback(async () => {
    const ttsService = ttsServiceRef.current;
    if (!ttsService) return;
    
    try {
      const voices = await ttsService.getAvailableVoices();
      const xaiVoices: XAIVoice[] = voices.map((voice) => ({
        id: voice.id,
        voiceURI: voice.id,
        name: voice.name,
        lang: voice.language,
        localService: false,
        default: false,
        gender: voice.name.includes("Female") ? "female" : "male",
      }));

      setAvailableVoices(xaiVoices);
      if (xaiVoices.length > 0 && !selectedVoice) {
        // Default to Domi (Warm Female) for softer ASMR-like reading
        const defaultVoice = xaiVoices.find(v => v.name.includes("Domi")) || xaiVoices[0];
        setSelectedVoice(defaultVoice);
      }
    } catch (error) {
      console.error('[Reading] Error loading voices:', error);
    }
  }, [selectedVoice, ttsServiceRef.current]);

  // Enable/disable microphone
  const toggleMicrophone = useCallback(async () => {
    if (!localParticipant) return;

    try {
      if (microphoneEnabled && microphoneTrack) {
        await localParticipant.unpublishTrack(microphoneTrack.track!);
        setMicrophoneTrack(null);
        setMicrophoneEnabled(false);
        console.log("[Reading] Microphone disabled");
      } else {
        const audioTrack = await createLocalAudioTrack();
        const publication = await localParticipant.publishTrack(audioTrack);
        setMicrophoneTrack(publication);
        setMicrophoneEnabled(true);
        console.log("[Reading] Microphone enabled");
      }
    } catch (error) {
      console.error("[Reading] Error toggling microphone:", error);
      setError("Failed to toggle microphone");
    }
  }, [localParticipant, microphoneEnabled, microphoneTrack]);

  useEffect(() => {
    loadVoices();

    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [loadVoices]);

  // Check room connection status
  useEffect(() => {
    if (!room) return;

    const handleConnectionStateChange = (state: string) => {
      console.log("Room connection state:", state);
      setConnected(state === "connected");
    };

    // Set initial state
    setConnected(room.state === "connected");

    room.on("connectionStateChanged", handleConnectionStateChange);

    return () => {
      room.off("connectionStateChanged", handleConnectionStateChange);
    };
  }, [room]);

  // Toggle play/pause with proper error handling
  const togglePlayPause = useCallback(async () => {
    const ttsService = ttsServiceRef.current;
    if (!ttsService) {
      console.warn("TTS service not initialized");
      return;
    }

    console.log('Play button clicked, isPlaying:', isPlaying);
    console.log('Content queue length:', ttsService.getContentQueue().length);
    console.log('Current section:', ttsService.getCurrentSection());

    try {
      if (isPlaying) {
        console.log('Pausing TTS');
        ttsService.pause();
        (ttsService as any).stopProgressUpdates?.();
        setIsPlaying(false);
        setCurrentWordIndex(-1); // Reset word highlighting when paused
      } else {
        console.log('Starting TTS with voice:', selectedVoice?.id || "AZnzlk1XvdvUeBnXmlld");
        await ttsService.speak({ voice: selectedVoice?.id || "AZnzlk1XvdvUeBnXmlld" }, currentIndex);
        (ttsService as any).startProgressUpdates?.();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Error toggling play/pause:", error);
      setError("Failed to toggle playback");
    }
  }, [isPlaying, selectedVoice, currentIndex]);

  // Seek to a specific section in the content
  const seekToSection = useCallback(async (index: number) => {
    const ttsService = ttsServiceRef.current;
    if (!ttsService) {
      console.warn("TTS service not initialized");
      return;
    }

    try {
      const wasPlaying = isPlaying;
      
      // Stop current playback and progress updates
      ttsService.stop();
      setIsPlaying(false);
      setCurrentWordIndex(-1); // Reset word highlighting when seeking
      (ttsService as any).stopProgressUpdates?.();
      
      // Wait a moment for cleanup
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Update to new section
      setCurrentIndex(index);
      const contentQueue = ttsService.getContentQueue();
      if (contentQueue[index]) {
        setCurrentSection(contentQueue[index]);
        setCurrentWordIndex(-1); // Reset to -1 to indicate no highlighting
        
        // Update progress
        const newProgress = index / contentQueue.length;
        setProgress(newProgress);
        setContinuousProgress(newProgress);
        
        // Use seekTo method instead of manual stop/speak
        ttsService.seekTo(index, { voice: selectedVoice?.id || "AZnzlk1XvdvUeBnXmlld" });
        
        // Start playing from new section if was playing
        if (wasPlaying) {
          await new Promise(resolve => setTimeout(resolve, 100)); // Brief delay
          await ttsService.speak({ 
            voice: selectedVoice?.id || "AZnzlk1XvdvUeBnXmlld"
          }, index);
          (ttsService as any).startProgressUpdates?.();
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error("Error seeking to section:", error);
      setError("Failed to seek to section");
    }
  }, [isPlaying, selectedVoice]);

  // Handle voice selection with proper type safety
  const handleVoiceSelect = useCallback((voice: XAIVoice) => {
    const ttsService = ttsServiceRef.current;
    if (!ttsService) {
      console.warn("TTS service not initialized");
      return;
    }

    // Type assertion to access potentially undefined method
    const service = ttsService as any;
    if (typeof service.setVoice === "function") {
      service.setVoice(voice);
    } else {
      console.warn("setVoice method not available on TTS service");
    }
    setSelectedVoice(voice);
  }, []);
  
  // Function to highlight current word being read
  const highlightCurrentWord = useCallback((text: string, charIndex: number) => {
    if (charIndex < 0 || charIndex >= text.length) return <span>{text}</span>;
    
    const beforeHighlight = text.substring(0, charIndex);
    const afterHighlight = text.substring(charIndex);
    
    // Find the current word boundary - match word characters, punctuation, or whitespace
    const wordMatch = afterHighlight.match(/^\S+/);
    if (!wordMatch) {
      // If no word match, highlight the next few characters or until next space
      const nextSpaceIndex = afterHighlight.indexOf(' ');
      const highlightLength = nextSpaceIndex > 0 ? nextSpaceIndex : Math.min(10, afterHighlight.length);
      const currentWord = afterHighlight.substring(0, highlightLength);
      const restOfText = afterHighlight.substring(highlightLength);
      
      return (
        <span>
          {beforeHighlight}
          <span className="bg-yellow-300 px-1 py-0.5 rounded shadow-sm font-medium transition-colors duration-200">{currentWord}</span>
          {restOfText}
        </span>
      );
    }
    
    const currentWord = wordMatch[0];
    const restOfText = afterHighlight.substring(currentWord.length);
    
    return (
      <span>
        {beforeHighlight}
        <span className="bg-yellow-300 px-1 py-0.5 rounded shadow-sm font-medium transition-colors duration-200 animate-pulse">{currentWord}</span>
        {restOfText}
      </span>
    );
  }, []);

  // Load book content only when room is connected and TTS service is ready
  useEffect(() => {
    if (!isConnected || !id || !books.length || !ttsServiceRef.current) {
      console.log("Book loading conditions not met:", {
        isConnected,
        id: !!id,
        books: books.length,
        tts: !!ttsServiceRef.current,
      });
      return;
    }

    const loadBook = async () => {
      try {
        const selectedBook = books.find((b) => b.id === id) as
          | BookWithContent
          | undefined;
        if (!selectedBook) {
          throw new Error("Book not found");
        }

        console.log("Loading book:", selectedBook.title);
        setBook(selectedBook);
        setCurrentSection("Loading book content...");

        // Load content into TTS service
        await ttsServiceRef.current!.loadContent(selectedBook);
        const queue = ttsServiceRef.current!.getContentQueue() || [];
        setTotalSections(queue.length);
        if (queue.length > 0) {
          setCurrentSection(queue[0]);
        }
        console.log("Book loaded successfully, sections:", queue.length);
      } catch (err) {
        console.error("Error loading book:", err);
        setError(err instanceof Error ? err.message : "Failed to load book");
      } finally {
        setLoading(false);
      }
    };

    loadBook();
  }, [isConnected, id, books, ttsServiceRef.current]);

  // Handle track subscription with proper cleanup
  useEffect(() => {
    if (!room) return;

    let isMounted = true;

    const handleTrackSubscribed = (
      track: any,
      publication: any,
      participant: RemoteParticipant
    ) => {
      if (!isMounted) return;
      console.log(
        "Track subscribed:",
        track.kind,
        track.sid,
        "from",
        participant.identity
      );
      // Force re-render to show new tracks
      setTrackVersion((v) => v + 1);
    };

    const handleTrackUnsubscribed = (
      track: any,
      publication: any,
      participant: RemoteParticipant
    ) => {
      if (!isMounted) return;
      console.log(
        "Track unsubscribed:",
        track.kind,
        track.sid,
        "from",
        participant.identity
      );
      // Force re-render to remove unsubscribed tracks
      setTrackVersion((v) => v + 1);
    };

    // Add event listeners
    room
      .on("trackSubscribed", handleTrackSubscribed)
      .on("trackUnsubscribed", handleTrackUnsubscribed);

    return () => {
      isMounted = false;
      // Remove event listeners
      room
        .off("trackSubscribed", handleTrackSubscribed)
        .off("trackUnsubscribed", handleTrackUnsubscribed);
    };
  }, [room]);

  // Handle TTS playback progress and section updates
  useEffect(() => {
    const service = ttsServiceRef.current;
    if (!service) return;

    const updateProgress = (progress: number) => {
      setProgress(progress);
      // Update current section based on progress
      const contentQueue = service.getContentQueue();
      const currentSectionIndex = Math.floor(progress * contentQueue.length);
      if (currentSectionIndex !== currentIndex && contentQueue[currentSectionIndex]) {
        setCurrentIndex(currentSectionIndex);
        setCurrentSection(contentQueue[currentSectionIndex]);
      }
    };

    service.onProgress(updateProgress);
    service.setProgressCallback(updateProgress);
    
    return () => {
      const currentService = ttsServiceRef.current;
      if (currentService) {
        currentService.offProgress();
      }
    };
  }, [currentIndex]);

  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="p-4 text-red-600">
        <h2 className="text-xl font-bold mb-2">Error</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  // Render main content
  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-6 flex justify-between items-center">
        <h1 className="text-xl font-semibold text-gray-800">
          {book?.title || "Reading"}
        </h1>
        <div className="flex items-center space-x-4">
          <HeaderIconsWithDropdowns />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-auto p-6">
        <div
          ref={readingAreaRef}
          className="prose max-w-3xl mx-auto bg-white p-6 rounded-lg shadow"
        >
          {currentSection ? (
            <div className="text-gray-700 leading-relaxed text-lg">
              {isPlaying && currentWordIndex >= 0 
                ? highlightCurrentWord(currentSection, currentWordIndex)
                : <span>{currentSection}</span>
              }
            </div>
          ) : (
            <p className="text-gray-500 italic">No content available</p>
          )}
        </div>
      </main>

      {/* Player controls */}
      <footer className="bg-white border-t border-gray-200 p-4">
        <div className="max-w-3xl mx-auto">
          {/* Progress bar */}
          <div className="mb-2">
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full bg-blue-500 ${isPlaying ? 'transition-all duration-100' : 'transition-all duration-300'}`}
                style={{ width: `${continuousProgress * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{formatTime(continuousProgress * totalSections)}</span>
              <span>{formatTime(totalSections)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center space-x-6">
            <button
              onClick={() => seekToSection(Math.max(0, currentIndex - 1))}
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
              disabled={currentIndex <= 0}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.333 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z"
                />
              </svg>
            </button>

            <button
              onClick={togglePlayPause}
              className="p-3 bg-blue-500 text-white rounded-full hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isPlaying ? (
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-8 h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </button>

            <button
              onClick={() =>
                seekToSection(Math.min(totalSections - 1, currentIndex + 1))
              }
              className="p-2 text-gray-600 hover:text-gray-900 disabled:opacity-50"
              disabled={currentIndex >= totalSections - 1}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Microphone toggle */}
            <button
              onClick={toggleMicrophone}
              className={`p-2 rounded-full ${
                microphoneEnabled
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-gray-200 text-gray-600 hover:bg-gray-300"
              } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                microphoneEnabled ? "focus:ring-red-500" : "focus:ring-gray-500"
              }`}
              title={
                microphoneEnabled ? "Disable microphone" : "Enable microphone"
              }
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {microphoneEnabled ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.586 15H4a1 1 0 01-1-1v-3a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                )}
              </svg>
            </button>

            {/* Voice selection */}
            <div className="ml-4">
              <select
                value={selectedVoice?.id || ""}
                onChange={(e) => {
                  const voice = availableVoices.find(
                    (v) => v.id === e.target.value
                  );
                  if (voice) handleVoiceSelect(voice);
                }}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {availableVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {voice.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </footer>

      {/* Audio elements for LiveKit tracks */}
      <div className="hidden">
        {trackReferences.map((trackRef) => {
          const { track } = trackRef.publication;
          if (!track) return null;

          return (
            <audio
              key={trackRef.publication.trackSid}
              ref={(ref) => {
                if (ref) {
                  track.attach(ref);
                } else if (track.detach) {
                  track.detach();
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

// Helper function to format time (seconds to MM:SS)
function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export default Reading;
